import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PiggyBank, ChevronDown, ChevronUp, Loader2, Gauge, CalendarCheck, X, CalendarClock, FileDown, TrendingDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI } from '../services/api';
import { useT } from '../services/i18n';
import BudgetHistoryChart from './BudgetHistoryChart';

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;
const today = () => new Date().toISOString().slice(0, 10);
const fmtMonth = (iso, lang) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { month: 'short', year: 'numeric' }) : '');

const fmtKm = (n) => (n == null ? '' : `${Math.round(n).toLocaleString('fr-FR')} km`);

// Service log line: « replaced on … » (date + km, default today / estimated mileage) → next due date (earliest of date and km), item leaves the 12-month budget until then
const ReplacedCell = ({ vehicleId, post, vehicle, onChanged, t, lang }) => {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(post.last_replaced || today());
  const [km, setKm] = useState(post.mileage_at ?? (vehicle.mileage != null ? Math.round(vehicle.mileage) : ''));
  const [currentKm, setCurrentKm] = useState('');
  const [busy, setBusy] = useState(false);
  const toInt = (v) => (v === '' || v == null ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try { await vehiclesAPI.setReplaced(vehicleId, post.key, date, toInt(km), vehicle.mileage == null ? toInt(currentKm) : null); setEditing(false); onChanged(); toast.success(t('garage.logSaved')); }
    catch (e) { toast.error(e.response?.data?.detail || t('garage.logError')); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await vehiclesAPI.clearReplaced(vehicleId, post.key); onChanged(); } catch { toast.error(t('garage.logError')); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1" data-testid={`garage-log-form-${vehicleId}-${post.key}`}>
        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="border border-gray-300 rounded px-1.5 py-0.5 text-[11px]" data-testid={`garage-log-date-${vehicleId}-${post.key}`} />
        {post.interval_km ? <input type="number" min="0" step="100" value={km} onChange={(e) => setKm(e.target.value)} placeholder={t('garage.logKm')} title={t('garage.logKm')} className="border border-gray-300 rounded px-1.5 py-0.5 text-[11px] w-24" data-testid={`garage-log-km-${vehicleId}-${post.key}`} /> : null}
        {vehicle.mileage == null && post.interval_km ? <input type="number" min="0" step="100" value={currentKm} onChange={(e) => setCurrentKm(e.target.value)} placeholder={t('garage.logKmCurrent')} title={t('garage.logKmCurrent')} className="border border-amber-300 bg-amber-50 rounded px-1.5 py-0.5 text-[11px] w-28" data-testid={`garage-log-km-current-${vehicleId}-${post.key}`} /> : null}
        <button type="button" onClick={save} disabled={busy} className="text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 px-2 py-0.5 rounded-full" data-testid={`garage-log-save-${vehicleId}-${post.key}`}>{busy ? '…' : 'OK'}</button>
        <button type="button" onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600" aria-label="Annuler"><X size={12} /></button>
      </span>
    );
  }
  if (post.last_replaced) {
    const nextLabel = post.overdue ? t('garage.logOverdue') : t('garage.logNext', { d: fmtMonth(post.next_due, lang) });
    return (
      <span className="inline-flex flex-wrap items-center gap-1" data-testid={`garage-log-done-${vehicleId}-${post.key}`}>
        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${post.overdue ? 'bg-red-100 text-red-700' : post.due_within_12m ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
          {post.overdue ? <CalendarClock size={11} /> : <CalendarCheck size={11} />} {nextLabel}{post.next_due_km ? <span data-testid={`garage-log-next-km-${vehicleId}-${post.key}`}> (≈ {fmtKm(post.next_due_km)})</span> : null}
        </span>
        {post.mileage_at != null && <span className="text-[10px] text-gray-400" data-testid={`garage-log-km-at-${vehicleId}-${post.key}`}>{t('garage.logKmAt', { km: fmtKm(post.mileage_at) })}</span>}
        {post.log_source === 'order' && <span className="text-[10px] text-gray-400" data-testid={`garage-log-source-${vehicleId}-${post.key}`}>{t('garage.logFromOrder', { d: fmtMonth(post.last_replaced, lang) })}</span>}
        <button type="button" onClick={() => setEditing(true)} className="text-[11px] text-gray-500 underline" data-testid={`garage-log-edit-${vehicleId}-${post.key}`}>{t('garage.logEdit')}</button>
        {post.log_source === 'manual' && <button type="button" onClick={clear} disabled={busy} className="text-gray-400 hover:text-red-600" aria-label={t('garage.logClear')} data-testid={`garage-log-clear-${vehicleId}-${post.key}`}><X size={12} /></button>}
      </span>
    );
  }
  return <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 px-2 py-0.5 rounded-full" data-testid={`garage-log-add-${vehicleId}-${post.key}`}><CalendarCheck size={11} /> {t('garage.logReplaced')}</button>;
};

// Green badge for 30 days after a « budget drop » alert e-mail; click/hover lists the posts that got cheaper
const DropBadge = ({ d, t, lang, vid }) => {
  const [open, setOpen] = useState(false);
  const since = new Date(d.since).toLocaleDateString(lang === 'en' ? 'en-GB' : lang);
  return (
    <span className="relative inline-block align-middle" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold" data-testid={`garage-budget-drop-${vid}`}>
        <TrendingDown size={11} /> {t('garage.dropBadge', { amount: eur(d.drop), date: since })}
      </button>
      {open && (
        <span className="absolute left-0 top-full mt-1 z-20 w-72 rounded-lg border border-emerald-200 bg-white shadow-lg p-3 text-left text-xs font-normal text-gray-700" data-testid={`garage-budget-drop-details-${vid}`}>
          <p className="font-semibold text-emerald-800 mb-1">{eur(d.before)} → {eur(d.after)}{t('garage.budgetPerYear')} (−{d.drop_pct} %)</p>
          <ul className="space-y-1">
            {d.posts.map((p) => (
              <li key={p.key} className="flex justify-between gap-2">
                <span>{p.label}{p.cheapest && <> · <Link to={`/produit/${p.cheapest.slug || p.cheapest.id}`} onClick={(e) => e.stopPropagation()} className="text-orange-600 hover:underline">{p.cheapest.name}</Link> ({eur(p.cheapest.price)})</>}</span>
                <span className="text-emerald-700 whitespace-nowrap">−{eur(p.annual_gain)}{t('garage.budgetPerYear')}</span>
              </li>
            ))}
          </ul>
        </span>
      )}
    </span>
  );
};

const VehicleRow = ({ v, t, lang, onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-gray-200 rounded-lg bg-white" data-testid={`garage-budget-vehicle-${v.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-3 text-left" data-testid={`garage-budget-toggle-${v.id}`}>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">{v.label || `${v.brand} ${v.model}`}</p>
          <p className="text-xs text-gray-500 truncate">{v.brand} {v.model}{v.year ? ` · ${v.year}` : ''}{v.engine ? <span className="text-emerald-700"> · <Gauge size={10} className="inline -mt-0.5" /> {v.engine}</span> : ''}</p>
          <p className="text-[11px] text-gray-400">{t('garage.budgetCoverage', { n: v.posts.length, total: v.posts_total })}{v.tracked ? ` · ${t('garage.logTracked', { n: v.tracked })}` : ''}{v.mileage != null ? ` · ${t('garage.logMileage', { km: fmtKm(v.mileage) })}` : ''}</p>
        </div>
        <div className="text-right whitespace-nowrap">
          <p className="text-lg font-bold text-gray-900" data-testid={`garage-budget-median-${v.id}`}>{eur(v.annual_median)}<span className="text-xs font-normal text-gray-500">{t('garage.budgetPerYear')}</span></p>
          <p className="text-xs text-emerald-700">{t('garage.budgetEco')} {eur(v.annual_min)}</p>
          <p className="text-xs text-amber-800" data-testid={`garage-budget-upcoming-${v.id}`}>{t('garage.budgetUpcoming')} {eur(v.upcoming_12m)}</p>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {v.recent_drop && <div className="px-3 pb-2 -mt-1"><DropBadge d={v.recent_drop} t={t} lang={lang} vid={v.id} /></div>}
      {open && (
        <div className="border-t border-gray-100 p-3" data-testid={`garage-budget-detail-${v.id}`}>
          {v.posts.length === 0 ? <p className="text-xs text-gray-500">{t('garage.budgetNoParts')}</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500"><tr><th className="text-left py-1">{t('garage.budgetPost')}</th><th className="text-left py-1">{t('garage.budgetInterval')}</th><th className="text-right py-1">{t('garage.budgetEco')}</th><th className="text-right py-1">{t('garage.budgetEstimated')}</th><th className="text-right py-1">{t('garage.budgetPerYearCol')}</th><th className="text-left py-1 pl-3">{t('garage.logCol')}</th></tr></thead>
                <tbody>
                  {v.posts.map((p) => (
                    <tr key={p.key} className={`border-t border-gray-100 ${!p.due_within_12m ? 'opacity-60' : ''}`} data-testid={`garage-budget-post-${v.id}-${p.key}`}>
                      <td className="py-1.5 pr-2"><span className="font-medium text-gray-800">{p.label}</span>{p.qty > 1 ? <span className="text-gray-400"> ×{p.qty}</span> : null}<span className="block text-[10px] text-gray-400">{p.products} {t('garage.budgetParts')}</span>{p.additive && <Link to={`/produit/${p.additive.slug || p.additive.id}`} className="block text-[10px] text-amber-700 hover:underline mt-0.5" data-testid={`garage-budget-additive-${v.id}-${p.key}`}>{t('garage.additiveHint')} {p.additive.name} · {eur(p.additive.price)}</Link>}</td>
                      <td className="py-1.5 pr-2 text-gray-600 whitespace-nowrap">{t('garage.budgetEvery', { n: p.interval_years })}{p.interval_km ? <span className="block text-[10px] text-gray-400">{t('garage.logOrKm', { km: fmtKm(p.interval_km) })}</span> : null}</td>
                      <td className="py-1.5 pr-2 text-right text-emerald-700 whitespace-nowrap">{eur(p.price_min)}</td>
                      <td className="py-1.5 pr-2 text-right text-gray-700 whitespace-nowrap">{eur(p.price_median)}</td>
                      <td className="py-1.5 text-right font-semibold text-gray-900 whitespace-nowrap">{eur(p.annual_median)}</td>
                      <td className="py-1.5 pl-3"><ReplacedCell vehicleId={v.id} post={p} vehicle={v} onChanged={onChanged} t={t} lang={lang} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-[11px] text-gray-500">{t('garage.logHint')}</p>
          {v.posts.length > 0 && <BudgetHistoryChart vehicleId={v.id} t={t} lang={lang} />}
          {v.missing.length > 0 && <p className="mt-1 text-[11px] text-gray-400">{t('garage.budgetMissing')} {v.missing.join(', ')}</p>}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Link to={v.path} className="text-xs font-medium text-orange-600 hover:underline" data-testid={`garage-budget-link-${v.id}`}>{t('garage.budgetSeeParts')}</Link>
            <CarnetPdfButton vehicleId={v.id} t={t} />
            <CarnetEmailButton vehicleId={v.id} t={t} />
          </div>
        </div>
      )}
    </li>
  );
};

const CarnetPdfButton = ({ vehicleId, t }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await vehiclesAPI.carnetPdf(vehicleId); toast.success(t('garage.carnetPdfDone')); }
    catch { toast.error(t('garage.logError')); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={download} disabled={busy} data-testid={`garage-carnet-pdf-${vehicleId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 bg-white border border-amber-300 hover:bg-amber-100 disabled:opacity-50 px-3 py-1 rounded-full">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} {t('garage.carnetPdf')}
    </button>
  );
};

const CarnetEmailButton = ({ vehicleId, t }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await vehiclesAPI.carnetEmail(vehicleId); toast.success(t('garage.carnetEmailDone', { email: r.email })); }
    catch (e) { toast.error(e.response?.data?.detail || t('garage.logError')); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={send} disabled={busy} data-testid={`garage-carnet-email-${vehicleId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 bg-white border border-amber-300 hover:bg-amber-100 disabled:opacity-50 px-3 py-1 rounded-full">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {t('garage.carnetEmail')}
    </button>
  );
};

// Garage: estimated yearly maintenance budget per vehicle (wear items × replacement interval, median & cheapest compatible part) + service log
export const GarageBudgetCard = ({ vehiclesCount }) => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = () => vehiclesAPI.garageBudget(true).then(setData).catch(() => setError(true));
  useEffect(() => { setData(null); setError(false); load(); }, [vehiclesCount]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error || (data && !data.vehicles.length)) return null;
  return (
    <section className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="garage-budget">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-bold text-amber-900 inline-flex items-center gap-2"><PiggyBank size={18} className="text-amber-600" /> {t('garage.budgetTitle')}</h3>
          <p className="text-xs text-amber-800 mt-0.5">{t('garage.budgetHint')}</p>
        </div>
        {data && (
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-900" data-testid="garage-budget-total">{eur(data.annual_median)}<span className="text-xs font-normal text-amber-700">{t('garage.budgetPerYear')}</span></p>
            <p className="text-xs text-emerald-700" data-testid="garage-budget-total-eco">{t('garage.budgetEcoTotal')} {eur(data.annual_min)}</p>
            <p className="text-xs font-semibold text-amber-900" data-testid="garage-budget-total-upcoming">{t('garage.budgetUpcoming')} {eur(data.upcoming_12m)}</p>
          </div>
        )}
      </div>
      {!data ? <div className="flex justify-center py-3"><Loader2 className="animate-spin text-amber-500" size={20} /></div> : (
        <ul className="space-y-2">{data.vehicles.map((v) => <VehicleRow key={v.id || `${v.brand}-${v.model}`} v={v} t={t} lang={lang} onChanged={load} />)}</ul>
      )}
    </section>
  );
};
